import Icon from "@/Components/Icon/Icon";
import Tooltip from "@/Components/Tooltip/Tooltip";
import useLayoutData from "@/shared/hooks/useLayoutData";
import { controlFormContext, objectIsEmpty } from "@/shared/utils/helpers";
import { darken } from "@/shared/utils/color";
import { useEffect, useMemo, useState } from "react";
import { Controller, useFormContext } from "react-hook-form";
import { useTranslation } from "react-i18next";
import Textfield, { TextFieldProps } from "./Textfield";
import { cn } from "@/shared/utils/cn";
import { useLanguageTab } from "./LanguageTabs";

export interface TextfieldIntControlledProps extends TextFieldProps {
  name: string;
  label?: string;
  defaultValue?: any;
  placeholder?: string;
  required?: boolean;
  validationMessage?: string;
  type?: "text" | "number" | "date" | "time";
  disabled?: boolean;
}

const TextfieldIntControlled = ({
  name,
  label,
  defaultValue = {},
  placeholder,
  required = false,
  validationMessage,
  type,
  disabled,
  ...rest
}: TextfieldIntControlledProps) => {
  const formContext = useFormContext();
  const { franchiseLanguages, supportedLanguagesKey } = useLayoutData();
  controlFormContext(formContext);

  const {
    control,
    setError,
    clearErrors,
    formState: { isSubmitted, isSubmitting },
    setValue,
    errors,
  } = formContext;

  const { t } = useTranslation();
  const [ownSelected, setSelected] = useState(franchiseLanguages[0].key);
  const sharedSelected = useLanguageTab();
  const selected = sharedSelected ?? ownSelected;
  const [fields, setFields] = useState({
    ...supportedLanguagesKey,
    ...defaultValue,
  });

  useEffect(() => {
    if (!objectIsEmpty(defaultValue)) {
      setFields((prev) => ({ ...prev, ...defaultValue }));
    }
  }, [defaultValue]);

  const isValid = useMemo(() => {
    return Boolean(fields.all);
  }, [fields]);

  useEffect(() => {
    if (!isValid && required && (isSubmitted || isSubmitting)) {
      setValue(name, null);
      setError(name, { message: validationMessage || t("fieldRequired") });
    } else {
      setValue(name, !isValid && required ? null : fields);
      clearErrors(name);
    }
  }, [fields, isValid, isSubmitted, isSubmitting]);

  return (
    <div className="min-w-full">
      <div className="mb-4 flex min-w-full items-center justify-between">
        {label && <div className="font-semibold">{label} </div>}
        {!sharedSelected && (
          <div className="flex items-center">
            {franchiseLanguages
              .filter((l) => l.key !== "turkish")
              .map((language, i, arr) => (
                <Tooltip key={language.value} content={t(language.label)}>
                  <div>
                    <div
                      className={cn(
                        "cursor-pointer rounded-xl px-1 transition-colors ",
                        selected === language.key ? "bg-grey" : "bg-white "
                      )}
                      onClick={() => setSelected(language.key)}
                    >
                      {language.icon ? (
                        <Icon className="h-10 w-8" variants={language.icon} />
                      ) : (
                        t(language.label)
                      )}
                    </div>
                  </div>
                </Tooltip>
              ))}
          </div>
        )}
      </div>

      {franchiseLanguages.map((language) => (
        <div
          key={language.value}
          style={{ display: language.key === selected ? "block" : "none" }}
        >
          <Controller
            name={name}
            control={control}
            rules={{
              required: {
                value: required,
                message: validationMessage || t("fieldRequired"),
              },
            }}
            defaultValue={fields}
            render={() => {
              return (
                <Textfield
                  type={type}
                  name={`name-${language.value}`}
                  onChange={(e) =>
                    setFields((f) => ({
                      ...f,
                      [selected]: e.target.value,
                    }))
                  }
                  value={fields?.[selected] ? fields?.[selected] : ""}
                  placeholder={placeholder}
                  error={errors?.[`${name}`]?.message}
                  disabled={disabled}
                  {...rest}
                />
              );
            }}
          />
        </div>
      ))}
    </div>
  );
};

export default TextfieldIntControlled;
